// Source-of-truth contract:
//   - api.slice.posSessionActive  = transient server cache (last GET response)
//   - PosContext (this file)      = persisted local state for the active session
// Hooks that fetch (e.g. SelectTerminal/CloseSession) read the slice once, then
// write into Context. Anything that needs the *current* session reads Context.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const TERMINAL_KEY = "pos_terminal";
const SESSION_KEY = "pos_session";

const readJSON = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeJSON = (key, value) => {
  try {
    if (value == null) {
      sessionStorage.removeItem(key);
    } else {
      sessionStorage.setItem(key, JSON.stringify(value));
    }
  } catch {
    // ignore
  }
};

const PosContext = createContext(null);

export const PosProvider = ({ children }) => {
  const [terminal, setTerminalState] = useState(() => readJSON(TERMINAL_KEY));
  const [session, setSessionState] = useState(() => readJSON(SESSION_KEY));

  const setTerminal = useCallback((value) => {
    writeJSON(TERMINAL_KEY, value);
    setTerminalState(value);
  }, []);

  const setSession = useCallback((value) => {
    writeJSON(SESSION_KEY, value);
    setSessionState(value);
  }, []);

  const clearTerminal = useCallback(() => setTerminal(null), [setTerminal]);
  const clearSession = useCallback(() => setSession(null), [setSession]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === TERMINAL_KEY) setTerminalState(readJSON(TERMINAL_KEY));
      if (e.key === SESSION_KEY) setSessionState(readJSON(SESSION_KEY));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo(
    () => ({ terminal, session, setTerminal, setSession, clearTerminal, clearSession }),
    [terminal, session, setTerminal, setSession, clearTerminal, clearSession]
  );

  return <PosContext.Provider value={value}>{children}</PosContext.Provider>;
};

export const usePos = () => {
  const ctx = useContext(PosContext);
  if (!ctx) {
    throw new Error("usePos must be used inside <PosProvider>");
  }
  return ctx;
};
